// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { inject, observer } from 'mobx-react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import List from 'react-virtualized/dist/commonjs/List'
import InfiniteLoader from 'react-virtualized/dist/commonjs/InfiniteLoader'
import WindowScroller from 'react-virtualized/dist/commonjs/WindowScroller'
import CellMeasurer, { CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer'
import { useMediaQuery } from 'react-responsive'

import Order from './Order'
import EmptyOrder from './EmptyOrder'
import { getTranslatedTextByKey } from 'utils/utils'
import { breakpoints, respondAbove, respondTo } from 'styles/mixins'

import 'react-virtualized/styles.css'
import SectionTitleWithDivider from 'components/common/SectionTitleWithDivider'
import { useRouter } from 'next/router'

let remoteRowCount = 0
let list = []
let isNextPageLoading = false
let nextPage = ''
let header
let tenantId = ''
const mobileHeaderHeight = 48
const mobileTitleHeight = 26.39
const desktopHeaderHeight = 83
const desktopTitleHeight = 40.8
const marginFromTitle = 20

const amountToDecreaseFromWindowHeightDesktop = desktopTitleHeight + desktopHeaderHeight + marginFromTitle
const amountToDecreaseFromWindowHeightMobile = mobileTitleHeight + mobileHeaderHeight + marginFromTitle
function isRowLoaded(index, itemsPerRow) {
	return !!list[index * itemsPerRow] || remoteRowCount <= index * itemsPerRow
}

const ListContainer = styled(WindowScroller)``

const Row = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	column-gap: 20px;
	overflow: visible;

	${respondAbove.lg`
		justify-content: space-between;
  	`}
`

const StyledList = styled(List)`
	margin-top: 35.5px;
	margin-bottom: 20px;
	overflow: visible !important;

	.ReactVirtualized__Grid__innerScrollContainer {
		overflow: visible !important;
	}

	${respondAbove.lg`
		margin-top: 43px;
	`}
`

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

	${respondAbove.lg`
		width: 70%;
  	`}

	${respondAbove.xl`
	  padding: unset;
	  width: 816px;
	`}
`

const TitleContainer = styled.div`
	${respondTo.sm`
		padding: 0 20px;
	`}
`
const PastOrdersSectionTitleWithDivider = ({ title }) => (
	<TitleContainer>
		<SectionTitleWithDivider title={title} />
	</TitleContainer>
)

export const PastOrdersContainer = inject(
	'Infra',
	'Account',
	'Application',
	'User'
)(
	observer((props) => {
		const router = useRouter()
		const { getAuthorizationHeader } = props.Account
		const { User, Application } = props
		const [isInitialized, setIsInitialized] = useState(false)
		const isMobileView = useMediaQuery({ query: `(max-width: ${breakpoints.sm})` })
		const itemsPerRow = isMobileView ? 1 : 2
		tenantId = props.Infra.appParams.c
		let _listRef
		const rowHeight = isMobileView ? 199 : 212
		const rowCount = Math.ceil(remoteRowCount / itemsPerRow)
		const isListEmpty = list.length === 0
		const cache = new CellMeasurerCache({
			defaultHeight: rowHeight,
			fixedWidth: true,
			minHeight: rowHeight,
		})

		const loadFirstPage = async (limit) => {
			const firstPageResponse = await User.fetchFirstPastOrdersPage(true, header, tenantId, limit)
			remoteRowCount = firstPageResponse.total
			nextPage = firstPageResponse.paging.next
			list = firstPageResponse.data
			isNextPageLoading = false
		}

		const loadMoreRows = async () => {
			// Only load 1 page of items at a time.
			// Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
			try {
				if (isNextPageLoading) {
					return {}
				}
				isNextPageLoading = true
				const nextPageResponse = await User.fetchNextPastOrdersPage(false, header, nextPage)
				list.push(...nextPageResponse.data)
				nextPage = nextPageResponse.paging.next
				isNextPageLoading = false
			} catch (error) {
				console.error(error)
			}
		}

		useEffect(() => {
			const getData = async () => {
				try {
					header = await getAuthorizationHeader(Application.backendChannel)
					// calculating the amount that needs to be decreased (header height and title height) from the window height
					const amountToDecreaseFromWindowHeight = isMobileView
						? amountToDecreaseFromWindowHeightMobile
						: amountToDecreaseFromWindowHeightDesktop
					const scrollHeight = window.innerHeight - amountToDecreaseFromWindowHeight
					const limit = Math.round(scrollHeight / rowHeight)
					isNextPageLoading = true
					await loadFirstPage(limit * itemsPerRow)
				} catch (e) {
					console.error(e)
				}
				setIsInitialized(true)
			}
			getData()
		}, [])

		const _setListRef = (ref, registerChild) => {
			_listRef = ref
			registerChild(ref)
		}

		const rowRenderer = ({ index, key, parent, style }) => {
			const items = []
			const fromIndex = index * itemsPerRow
			const toIndex = Math.min(fromIndex + itemsPerRow, remoteRowCount)

			for (let j = fromIndex; j < toIndex; j++) {
				items.push(<Order orderData={list[j]} key={j} />)
			}

			return (
				<CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
					<Row key={key} style={style}>
						{items}
					</Row>
				</CellMeasurer>
			)
		}

		const handleClickEmptyOrder = () => {
			router.push('/')
		}

		return (
			isInitialized &&
			(isListEmpty ? (
				<Container>
					<PastOrdersSectionTitleWithDivider title={getTranslatedTextByKey('eCommerce.accountSettings.pastOrders', 'Past Orders')} />
					<EmptyOrder onClick={handleClickEmptyOrder} />
				</Container>
			) : (
				<Container>
					<PastOrdersSectionTitleWithDivider title={getTranslatedTextByKey('eCommerce.accountSettings.pastOrders', 'Past Orders')} />
					<InfiniteLoader isRowLoaded={({ index }) => isRowLoaded(index, itemsPerRow)} loadMoreRows={loadMoreRows} rowCount={rowCount + 1}>
						{({ onRowsRendered, registerChild }) => (
							<ListContainer>
								{({ height, isScrolling, scrollTop }) => (
									<AutoSizer disableHeight>
										{({ width }) => (
											<StyledList
												height={height}
												deferredMeasurementCache={cache}
												onRowsRendered={onRowsRendered}
												rowCount={rowCount}
												rowHeight={cache.rowHeight}
												rowRenderer={rowRenderer}
												width={width}
												ref={(ref) => _setListRef(ref, registerChild)}
												overscanRowCount={itemsPerRow}
												scrollTop={scrollTop}
												autoHeight
											/>
										)}
									</AutoSizer>
								)}
							</ListContainer>
						)}
					</InfiniteLoader>
				</Container>
			))
		)
	})
)
